import React from 'react';
import { Container, Link, Stack, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MDBox from '../../atoms/MDBox';
import MDTypography from '../../atoms/MDTypography';
import { LEGAL_TEXTS } from '../../../../utils/texts';
import { getBrandName } from '../../../../utils/brandUtils';
import { getCurrentYear } from '../../../../utils/dateUtils';

interface FooterProps {
  variant?: 'public' | 'auth' | 'sidebar';
}

const Footer: React.FC<FooterProps> = ({ variant = 'public' }) => {
  const navigate = useNavigate();
  const appName = getBrandName();
  const currentYear = getCurrentYear();

  const handleLegalClick = (path: string) => {
    navigate(path);
  };

  if (variant === 'sidebar') {
    return (
      <MDBox
        component="footer"
        sx={{
          p: 1.5,
          backgroundColor: 'transparent'
        }}
      >
        <Stack spacing={1} alignItems="center">
          {/* Legal Links */}
          <Stack 
            direction="column" 
            spacing={0.5}
            alignItems="center"
            sx={{ width: '100%' }}
          >
            <Link
              component="button"
              variant="caption"
              onClick={() => handleLegalClick('/cgu')}
              sx={{
                textDecoration: 'none',
                color: 'text.secondary',
                opacity: 0.8,
                cursor: 'pointer',
                fontSize: '0.7rem',
                padding: '2px 4px',
                borderRadius: '4px',
                '&:hover': {
                  opacity: 1,
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {LEGAL_TEXTS.cgu}
            </Link>
            
            {/* Placeholder for future Privacy Policy */}
            <MDTypography 
              variant="caption" 
              color="text" 
              opacity={0.4}
              sx={{ 
                cursor: 'not-allowed',
                fontSize: '0.7rem'
              }}
            >
              {LEGAL_TEXTS.privacyPolicy}
            </MDTypography>
          </Stack>

          {/* Copyright */}
          <MDTypography 
            variant="caption" 
            color="text" 
            opacity={0.5}
            textAlign="center"
            sx={{ 
              fontSize: '0.65rem',
              lineHeight: 1.2,
              mt: 0.5
            }}
          >
            © {currentYear} {appName}
          </MDTypography>
        </Stack>
      </MDBox>
    );
  }

  return (
    <MDBox
      component="footer"
      sx={{
        mt: 'auto',
        py: 3,
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: (theme) => 
          variant === 'auth' 
            ? theme.palette.background.paper 
            : 'transparent'
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          {/* Copyright */}
          <MDTypography 
            variant="caption" 
            color="text" 
            opacity={0.7}
            textAlign={{ xs: 'center', sm: 'left' }}
          >
            {LEGAL_TEXTS.footer.copyright} © {currentYear} {appName} - {LEGAL_TEXTS.footer.allRightsReserved}
          </MDTypography>

          {/* Legal Links - Only show for auth variant */}
          {variant === 'auth' && (
            <Stack 
              direction="row" 
              spacing={2} 
              divider={<Divider orientation="vertical" flexItem />}
              alignItems="center"
            >
              <Link
                component="button"
                variant="caption"
                onClick={() => handleLegalClick('/cgu')}
                sx={{
                  textDecoration: 'none',
                  color: 'text.secondary',
                  opacity: 0.7,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 1,
                    textDecoration: 'underline'
                  }
                }}
              >
                {LEGAL_TEXTS.cgu}
              </Link>
              
              {/* Placeholder for future Privacy Policy */}
              <MDTypography 
                variant="caption" 
                color="text" 
                opacity={0.5}
                sx={{ cursor: 'not-allowed' }}
              >
                {LEGAL_TEXTS.privacyPolicy}
              </MDTypography>
            </Stack>
          )}
        </Stack>
      </Container>
    </MDBox>
  );
};

export default Footer;